import { DEFAULT_STOCKPILE_TOOL_MODE, stockpileToolModeAtom } from '@/atoms/stockpile.atom';
import { sortVerticesByCentroidAngle } from '@/lib/stockpile/pointInPolygon';
import { Check, Trash, Undo } from '@mynaui/icons-react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import {
  FloatingToolbar,
  FloatingToolbarButton,
  FloatingToolbarSeparator,
} from './FloatingToolbar';

/**
 * Floating top-center toolbar that shows the current picking status and
 * exposes Undo / Finalize / Cancel actions while the stockpile tool is active.
 *
 * Visuals are provided by the shared `<FloatingToolbar>` so we stay
 * consistent with `<AddSurfaceToolbar>` and the elevation-profile drawing
 * hint. The stockpile-specific orange primary color is passed in via
 * `primaryClass`. Only renders during the `'picking'` phase — once the
 * polygon is closed, the `<StockpileComputePanel />` takes over.
 */
export const StockpileToolbar = () => {
  const [mode, setMode] = useAtom(stockpileToolModeAtom);
  const { t } = useTranslation('project');

  if (!mode.enabled || mode.phase === 'closed') return null;

  const canUndo = mode.pickedPoints.length > 0;
  const canFinalize = mode.pickedPoints.length >= 3;
  const status =
    mode.pickedPoints.length === 0
      ? t('stockpile.statusPickFirst')
      : mode.pickedPoints.length === 1
        ? t('stockpile.statusPickSecond')
        : mode.pickedPoints.length === 2
          ? t('stockpile.statusPickThird')
          : t('stockpile.statusPickMore', { count: mode.pickedPoints.length });

  const handleUndo = () => {
    if (mode.pickedPoints.length === 0) return;
    const next = mode.pickedPoints.slice(0, -1);
    setMode({
      ...mode,
      pickedPoints: next,
      sourceLayerId: next.length === 0 ? null : mode.sourceLayerId,
      phase: next.length === 0 ? 'idle' : 'picking',
    });
  };

  const handleFinalize = () => {
    if (!canFinalize) return;
    // Auto-sort vertices around centroid so click order doesn't matter — see
    // sortVerticesByCentroidAngle for the algorithm + caveats.
    setMode({
      ...mode,
      phase: 'closed',
      pickedPoints: sortVerticesByCentroidAngle(mode.pickedPoints),
    });
  };

  const handleCancel = () => {
    setMode({ ...DEFAULT_STOCKPILE_TOOL_MODE });
  };

  return (
    <FloatingToolbar status={status}>
      <FloatingToolbarButton
        icon={<Undo />}
        tooltip={t('stockpile.undo')}
        disabled={!canUndo}
        onClick={handleUndo}
      />
      <FloatingToolbarSeparator />
      <FloatingToolbarButton
        icon={<Check />}
        tooltip={t('stockpile.finalize')}
        intent='primary'
        primaryClass='bg-orange-500 hover:bg-orange-600'
        disabled={!canFinalize}
        onClick={handleFinalize}
      />
      <FloatingToolbarButton
        icon={<Trash />}
        tooltip={t('stockpile.cancel')}
        intent='danger'
        onClick={handleCancel}
      />
    </FloatingToolbar>
  );
};
